import React, { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

// Interactive 3D-tilt emblem that reacts to the pointer + gently floats.
export default function FloatingEmblem() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) {
      v.play().catch((e) => console.error("Video play failed:", e));
    }
    setMuted(next);
  };

  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 15 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 120, damping: 15 });
  const glowX = useTransform(mx, [-0.5, 0.5], ["35%", "65%"]);
  const glowY = useTransform(my, [-0.5, 0.5], ["35%", "65%"]);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid="hero-emblem"
      className="relative mx-auto w-full max-w-md aspect-square"
      style={{ perspective: 1000 }}
    >
      <motion.div
        className="absolute inset-0 rounded-full blur-2xl"
        style={{
          background: "radial-gradient(circle at var(--gx) var(--gy), rgba(212,175,55,0.35) 0%, transparent 60%)",
          "--gx": glowX,
          "--gy": glowY,
        }}
      />
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        animate={{ y: [0, -14, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative h-full w-full"
      >
        <motion.video
          ref={videoRef}
          src="/unerasable.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-label="7 Generations Indigenous Trust — Unerasable"
          className="h-full w-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.8)]"
          style={{ translateZ: 40 }}
        />
      </motion.div>

      <button
        onClick={toggleSound}
        data-testid="emblem-sound-toggle"
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute bottom-3 right-3 z-10 flex items-center justify-center w-11 h-11 rounded-full border border-gold/40 bg-black/60 backdrop-blur-md text-gold hover:bg-gold hover:text-black transition-colors duration-300"
      >
        {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
      </button>
    </div>
  );
}
